/**
 * Device enumeration/selection for `SettingsPanel`.
 *
 * The browser only exposes labels after a permission grant, so `listDevices` falls back to ordinal
 * labels and `requestPermission` is the explicit user gesture that unlocks real names. Every failure
 * is surfaced as an `RtcError` (`RTC_DEVICE_NOT_FOUND` for permission/absence problems).
 */
import { RtcError, normalizeError } from '@nimeet/rtc-web';
import { fail, ok } from '../internal/result.js';
const STORAGE_KEY = 'nimeet.ui-kit.device-selection';
function defaultPort() {
    const media = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
    if (!media || typeof media.enumerateDevices !== 'function')
        return null;
    return {
        enumerateDevices: async () => {
            const devices = await media.enumerateDevices();
            return devices.map((device) => ({
                deviceId: device.deviceId,
                kind: device.kind,
                label: device.label,
                groupId: device.groupId,
            }));
        },
        getUserMedia: (constraints) => media.getUserMedia(constraints),
    };
}
function defaultStorage() {
    try {
        if (typeof localStorage === 'undefined')
            return null;
        return localStorage;
    }
    catch {
        return null;
    }
}
function emptySelection(overrides) {
    return {
        microphoneId: overrides?.microphoneId ?? null,
        cameraId: overrides?.cameraId ?? null,
        speakerId: overrides?.speakerId ?? null,
    };
}
function readSelection(storage) {
    if (!storage)
        return emptySelection();
    try {
        const raw = storage.getItem(STORAGE_KEY);
        if (!raw)
            return emptySelection();
        const parsed = JSON.parse(raw);
        return emptySelection(parsed);
    }
    catch {
        return emptySelection();
    }
}
export class DeviceService {
    port;
    storage;
    listeners = new Set();
    selection;
    constructor(options = {}) {
        this.port = options.mediaDevices === undefined ? defaultPort() : options.mediaDevices;
        this.storage = options.storage === undefined ? defaultStorage() : options.storage;
        this.selection = options.selection ? emptySelection(options.selection) : readSelection(this.storage);
    }
    isSupported() {
        return this.port !== null;
    }
    getSelection() {
        return { ...this.selection };
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => {
            this.listeners.delete(listener);
        };
    }
    async listDevices() {
        if (!this.port) {
            return fail(new RtcError('RTC_DEVICE_NOT_FOUND', 'this environment does not expose media devices'));
        }
        try {
            const devices = await this.port.enumerateDevices();
            const group = { microphones: [], cameras: [], speakers: [] };
            let microphoneIndex = 0;
            let cameraIndex = 0;
            let speakerIndex = 0;
            for (const device of devices) {
                if (device.kind === 'audioinput') {
                    microphoneIndex += 1;
                    group.microphones.push({ ...device, label: device.label || `Microphone ${microphoneIndex}` });
                }
                else if (device.kind === 'videoinput') {
                    cameraIndex += 1;
                    group.cameras.push({ ...device, label: device.label || `Camera ${cameraIndex}` });
                }
                else if (device.kind === 'audiooutput') {
                    speakerIndex += 1;
                    group.speakers.push({ ...device, label: device.label || `Speaker ${speakerIndex}` });
                }
            }
            return ok(group);
        }
        catch (error) {
            return fail(this.wrap(error));
        }
    }
    /** Prompts for microphone and/or camera access; stops the probe tracks immediately. */
    async requestPermission(kind = 'both') {
        if (!this.port || !this.port.getUserMedia) {
            return fail(new RtcError('RTC_DEVICE_NOT_FOUND', 'media capture is not available in this environment'));
        }
        try {
            const stream = await this.port.getUserMedia({
                audio: kind === 'audio' || kind === 'both',
                video: kind === 'video' || kind === 'both',
            });
            for (const track of stream.getTracks())
                track.stop();
            return ok(undefined);
        }
        catch (error) {
            return fail(this.wrap(error));
        }
    }
    select(kind, deviceId) {
        if (kind === 'microphone')
            this.selection.microphoneId = deviceId;
        else if (kind === 'camera')
            this.selection.cameraId = deviceId;
        else
            this.selection.speakerId = deviceId;
        this.persist();
        this.notify();
        return this.getSelection();
    }
    /** Select a device by its `MediaDeviceKindName`. */
    selectByKind(kind, deviceId) {
        return this.select(kind === 'audioinput' ? 'microphone' : kind === 'videoinput' ? 'camera' : 'speaker', deviceId);
    }
    dispose() {
        this.listeners.clear();
    }
    persist() {
        if (!this.storage)
            return;
        try {
            this.storage.setItem(STORAGE_KEY, JSON.stringify(this.selection));
        }
        catch {
            // Storage can be unavailable (private mode); selection still works for the session.
        }
    }
    notify() {
        for (const listener of [...this.listeners])
            listener();
    }
    wrap(error) {
        const normalized = normalizeError(error);
        if (normalized.code === 'internal_error') {
            return new RtcError('RTC_DEVICE_NOT_FOUND', normalized.message, { cause: error });
        }
        return normalized;
    }
}
//# sourceMappingURL=device-service.js.map